<script lang="ts" setup>import { computed as _computed } from 'vue';

import {
  AppButton,
  Checkbox,
  Formula,
  Heading
} from '@pohlcon/design-system';
import { reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { matomoAPI } from '~/APIConnection';

import { emailCreator } from '~/helpers/emailCreator';
import type { InstallationSetup } from '~/models';
import { useMainStore } from '~/store';

const { t } = useI18n();
const router = useRouter();
const mainStore = useMainStore();

interface Props {
  /** */
  installationSetup: InstallationSetup;
}

const props = defineProps<Props>();

defineEmits<{
  (e: 'update:modelValue', model: InstallationSetup): void;
}>();

const optionValues = reactive({
  lambda: props.installationSetup.product.lambdaValue ? true : false
});

const data = reactive({
  showOptions: false,
});

const emailConfig = _computed(() => {
  matomoAPI.generalControllerLambdaProduct({
    rowKey: props.installationSetup.product.rowKey
  });
  return emailCreator(props.installationSetup.product, [
    t('printView.lambdaRequest.part1'),
    t('printView.lambdaRequest.part2')
  ]);
});



const disabledChecks = _computed(() => {
  const values: string[] = [];
  if (!props.installationSetup.wallStructure.thicknesses) values.push('wallThickness');
  if (!props.installationSetup.product.heatFlow) values.push('heatFlow');
  if (!props.installationSetup.product.lambdaValue) values.push('lambda');
  // Category must be always available
  return values;
});

const options: Record<string, { title: string, hintOnBlock: string; }> = _computed(() => ({
  lambda: {
    title: t('printView.sidebar.options.lambda.title'),
    hintOnBlock: t('printView.sidebar.options.lambda.hintOnBlock')
  }
}));

function print (): void {
  matomoAPI.generalControllerPrintOutProduct({
    rowKey: props.installationSetup.product.rowKey
  });
  window.print();
}

const restart = () => {
  mainStore.resetState();
  router.push('/');
};

const goToPreviousStep = async () => {
  await mainStore.setPreviousStep();
  router.push(mainStore.currentStep.to);
};
const handleEmail = () => {
  const url = new URL(emailConfig.value);
  window.location.href = url.toString();
};
</script>

<template>
  <section id="print-out__options">
    <div
      class="sidebar"
      :class="{ open: data.showOptions }"
      :style="{ 'grid-area': 'sidebar' }"
    >
      <div
        class="sidebar__item options"
        :style="{ 'grid-area': 'options' }"
      >
        <Heading :level="4">{{ t('printView.sidebar.title') }}</Heading>
        <Checkbox
          :cypress-id="`print-view-option-${i}`"
          v-for="(option, i) in options"
          v-model="optionValues[i as keyof typeof optionValues]"
          :key="i"
          :labelPosition="'labelLeading'"
          :labelCategory="'default'"
          :disabled="disabledChecks.includes(i)"
          :title="disabledChecks.includes(i) ? option.hintOnBlock : null"
        >
          <template #label>
            <div class="option__title">
              {{ option.title }}
              <!-- TODO: use Formula color for locked state -->
              <Formula
                v-if="i === 'lambda'"
                latex="\lambda_{eq}"
                size="small"
                font="Verdana"
              />
            </div>
          </template>
        </Checkbox>
        <!-- if catAThresh fetch errors occur, there might be a lambda that won't fit B, but A,
        no catAThresh leads to no category mistakenly and showed 'request lambda'-->
        <AppButton
          v-if="!installationSetup.product.lambdaValue"
          class="request-lambda"
          @click="handleEmail"
        >
          {{ t('printView.sidebar.actions.no_lambda') }}
        </AppButton>
      </div>
      <div
        class="sidebar__item actions"
        :style="{ 'grid-area': 'actions' }"
      >
        <div
          class="router-actions"
          :style="{ 'grid-area': 'router' }"
        >
          <AppButton
            :text="t('actions.restart')"
            secondary
            icon="home"
            @click="restart"
            fillContainer
            cypress-id="print-view-restart-button"
          />
          <AppButton
            :text="t('actions.back')"
            secondary
            icon="chevron_left"
            @click="goToPreviousStep"
            fillContainer
            cypress-id="print-view-back-button"
          />
        </div>
        <AppButton
          :style="{ 'grid-area': 'action' }"
          :text="t('printView.sidebar.actions.with_lambda')"
          icon="file"
          @click="print()"
          cypress-id="print-view-print-button"
        />
      </div>
    </div>
    <div
      class="toggle"
      :style="{ 'grid-area': 'toggle' }"
    >
      <AppButton
        @click="data.showOptions = !data.showOptions"
        cypress-id="print-view-toggle-button"
      >
        <i v-if="data.showOptions">&lang;</i>
        <i v-else>&rang;</i>
      </AppButton>
    </div>
    <div
      id="print-out__preview"
      :class="{ open: !data.showOptions }"
      :style="{ 'grid-area': 'preview' }"
    >
      <slot :value="optionValues"></slot>
    </div>
  </section>
</template>

<style lang="scss">
@use '../../variables' as *;

#print-out {
  &__options {
    display: grid;
    grid-template:
      "sidebar preview" 2fr
      "sidebar preview" min-content/ max-content 1fr;
    column-gap: 20px;
    margin-top: 20px;
    max-height: calc(100vh - 180px);

    .toggle {
      display: none;

      button.btn {
        height: unset;
      }

      i {
        font-style: normal;
      }
    }
  }

  &__preview {
    border: 1px solid var(--bds-tertiaryText-color);
    overflow-y: scroll;
    padding-right: 0.25rem;
  }

  @media screen and (max-width: $breakpoint-desktop) {
    &__options {
      grid-template:
        "sidebar toggle preview" 1fr / min-content min-content 1fr;
      grid-gap: 0.5rem;

      .toggle {
        display: grid;
      }

      .sidebar {
        display: none;

        &.open {
          display: grid;

          &.options {
            display: inline;
          }
        }
      }

      @media screen and (max-width: $breakpoint-tablet) {
        max-height: calc(100vh - 80px);
      }
    }
  }
}

.sidebar {
  display: grid;
  white-space: nowrap;
  grid-template:
    "options" 1fr
    "actions" min-content / min-content;
  height: 100%;
  width: 100%;
  grid-gap: 20px;

  &__item {
    padding: var(--gutter-m);
    border: 1px solid var(--bds-tertiaryText-color);

    &.options {
      .heading {
        margin-bottom: var(--gutter-m) !important;
      }

      label.label {
        grid-auto-flow: column;
        grid-template-columns: unset;

        &:last-of-type {
          margin-bottom: var(--gutter-m);
        }

        .option__title {
          display: flex;
          align-items: center;
          column-gap: var(--gutter-xs);
        }

        .label__fields {
          .checkbox .checkmark {
            align-items: unset;
          }
        }
      }
    }

    .request-lambda {
      display: inline-block;
    }

    &.actions {
      display: grid;
      gap: var(--gutter-m);
      justify-content: center;
      grid-auto-flow: column;
      grid-template:
        "router ." 1fr
        "router action" 1fr / 1fr;

      .router-actions {
        display: grid;
        gap: 10px;
        grid-auto-flow: row;
      }
    }
  }

  @media screen and (max-width: $breakpoint-tablet) {
    grid-gap: 10px;
  }
}
</style>
